// ============================================================================
// HAI Reporting Tool - Authentication Service
// Firebase Auth operations and user profile management
// ============================================================================

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  UserCredential
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { User, UserRole } from '@/types';
import { db as localDb } from './db';

/**
 * Sign up a new user with email and password
 */
export async function signUp(
  email: string,
  password: string,
  displayName: string,
  facilityId: string // Required: user must be assigned to a facility
): Promise<UserCredential> {
  try {
    // Create Firebase Auth user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Update display name
    await updateProfile(user, { displayName });

    // Create user profile in Firestore
    const userProfile: Omit<User, 'id'> = {
      displayName,
      email,
      role: 'Ward_Nurse', // Default role
      facilityId, // Assign to facility during signup
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
      lastLoginAt: new Date()
    };

    await setDoc(doc(db, 'users', user.uid), {
      ...userProfile,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastLoginAt: serverTimestamp()
    });

    // Cache user profile locally
    await localDb.users.put({ ...userProfile, id: user.uid });

    return userCredential;
  } catch (error) {
    console.error('Error signing up:', error);
    throw error;
  }
}

/**
 * Sign in with email and password
 */
export async function signIn(
  email: string,
  password: string
): Promise<UserCredential> {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);

    // Update last login timestamp
    await updateDoc(doc(db, 'users', userCredential.user.uid), {
      lastLoginAt: serverTimestamp()
    });

    // Fetch and cache user profile
    const profile = await getUserProfile(userCredential.user.uid);
    await localDb.users.put(profile);

    return userCredential;
  } catch (error) {
    console.error('Error signing in:', error);
    throw error;
  }
}

/**
 * Send password reset email
 */
export async function resetPassword(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error('Error sending password reset email:', error);
    throw error;
  }
}

/**
 * Get user profile from Firestore
 */
export async function getUserProfile(userId: string): Promise<User> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));

    if (!userDoc.exists()) {
      throw new Error('User profile not found');
    }

    const data = userDoc.data();
    return {
      id: userDoc.id,
      displayName: data.displayName,
      email: data.email,
      role: data.role as UserRole,
      facilityId: data.facilityId || '',
      isActive: data.isActive,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      lastLoginAt: data.lastLoginAt?.toDate() || new Date()
    };
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
}

/**
 * Update user profile
 */
export async function updateUserProfile(
  userId: string,
  updates: Partial<User>
): Promise<void> {
  try {
    const allowedUpdates: Partial<User> = {
      displayName: updates.displayName,
      // Role and facilityId should only be updated by admins
    };

    await updateDoc(doc(db, 'users', userId), {
      ...allowedUpdates,
      updatedAt: serverTimestamp()
    });

    // Update local cache
    const profile = await getUserProfile(userId);
    await localDb.users.put(profile);
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
}

/**
 * Check if user has permission for a specific role
 */
export function hasRole(user: User | null, roles: UserRole[]): boolean {
  if (!user) return false;
  return roles.includes(user.role);
}

/**
 * Check if user has facility access
 */
export function hasFacilityAccess(user: User | null, facilityId: string): boolean {
  if (!user) return false;
  if (user.role === 'Admin') return true; // Admins have access to all facilities
  return user.facilityId === facilityId;
}

/**
 * Get error message from Firebase Auth error
 */
export function getAuthErrorMessage(error: any): string {
  const errorCode = error?.code;

  switch (errorCode) {
    case 'auth/email-already-in-use':
      return 'This email is already registered. Please sign in instead.';
    case 'auth/invalid-email':
      return 'Invalid email address.';
    case 'auth/weak-password':
      return 'Password is too weak. Please use at least 6 characters.';
    case 'auth/user-not-found':
      return 'No account found with this email.';
    case 'auth/wrong-password':
      return 'Incorrect password.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection.';
    default:
      return 'An error occurred. Please try again.';
  }
}
